"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { API_URL } from "@/components/config/config";
import { auth } from "@/components/context/firebase";
import {
  CopilotApiError,
  getCopilotThread,
  postCopilotArrival,
  postCopilotMessage,
  updateCopilotProposal,
  type CopilotMessage,
  type CopilotStep,
  type CopilotThread,
} from "@/components/copilot/api";
import { t as translate } from "@/lib/i18n";
import type {
  CopilotDirective,
  NovaTarget,
} from "@/components/copilot/targets";

const DISABLE_AUTH =
  String(process.env.NEXT_PUBLIC_DISABLE_AUTH || "").toLowerCase() === "true";
const RECONNECT_DELAYS = [1_000, 2_000, 5_000, 10_000];
const ARRIVAL_WINDOW_MS = 10 * 60 * 1_000;
const TARGET_PATHS: Partial<Record<NovaTarget, string>> = {
  "nav.dashboard": "/dashboard/agency",
  "nav.roles": "/dashboard/agency/roles",
  "nav.screening": "/dashboard/agency/screening",
  "nav.interviews": "/dashboard/agency/interviews",
  "nav.reports": "/dashboard/agency/reports",
  "nav.recordings": "/dashboard/agency/calls",
  "nav.talent": "/dashboard/agency/talent-pool",
  "nav.library": "/dashboard/agency/assets",
  "nav.careerpage": "/dashboard/agency/company/career-page",
  "nav.emails": "/dashboard/agency/company/emails",
  "nav.integrations": "/dashboard/agency/company/integrations",
};

type SocketEvent =
  | { type: "step"; message_id: number; step: CopilotStep }
  | { type: "message"; message: CopilotMessage }
  | { type: "error"; message?: CopilotMessage; error?: string };

function socketUrl() {
  const base = API_URL || window.location.origin;
  const url = new URL("/ws/copilot/", `${base.replace(/\/+$/, "")}/`);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

function mergeMessage(messages: CopilotMessage[], message: CopilotMessage) {
  const existingIndex = messages.findIndex((item) => item.id === message.id);
  if (existingIndex === -1) return [...messages, message];
  return messages.map((item, index) => (index === existingIndex ? message : item));
}

function mergeStep(steps: CopilotStep[], step: CopilotStep) {
  const exists = steps.some(
    (item) =>
      item.tool === step.tool &&
      item.label === step.label &&
      item.status === step.status &&
      item.directive?.target === step.directive?.target &&
      item.directive?.message === step.directive?.message
  );
  return exists ? steps : [...steps, step];
}

function runningMessage(id: number, step: CopilotStep): CopilotMessage {
  const now = new Date().toISOString();
  return {
    id,
    role: "assistant",
    content: "",
    page: "",
    steps: [step],
    proposal: {},
    proposal_status: "",
    run_status: "running",
    error: null,
    created_at: now,
    updated_at: now,
    stale: false,
  };
}

export function useCopilotThread(
  enabled: boolean,
  pathname: string,
  onArrivalAccepted: () => void
) {
  const [thread, setThread] = useState<CopilotThread | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [proposalId, setProposalId] = useState<number | string | null>(null);
  const [notice, setNotice] = useState("");
  const [socketRejected, setSocketRejected] = useState(false);
  const [activeDirective, setActiveDirective] =
    useState<CopilotDirective | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const sentMessageIdsRef = useRef(new Set<number>());
  const shownDirectiveKeysRef = useRef(new Set<string>());
  const pendingDirectiveRef = useRef<{
    key: string;
    target: NovaTarget;
    pathname: string;
    receivedAt: number;
  } | null>(null);
  const firedDirectiveKeysRef = useRef(new Set<string>());
  const flowArmedRef = useRef<{ at: number } | null>(null);
  const firedFlowArrivalsRef = useRef(new Set<string>());
  const arrivalPathnameRef = useRef(pathname);
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  const surfaceDirective = useCallback(
    (messageId: number | string, step: CopilotStep) => {
      if (!step.directive) return;
      const key = `${messageId}:${step.directive.target}:${step.directive.message}`;
      if (shownDirectiveKeysRef.current.has(key)) return;
      shownDirectiveKeysRef.current.add(key);
      pendingDirectiveRef.current = {
        key,
        target: step.directive.target,
        pathname: pathnameRef.current,
        receivedAt: Date.now(),
      };
      setActiveDirective(step.directive);
    },
    []
  );

  const surfaceReplyDirectives = useCallback(
    (messages: CopilotMessage[]) => {
      if (!sentMessageIdsRef.current.size) return;
      messages.forEach((message) => {
        if (
          message.role !== "assistant" ||
          ![...sentMessageIdsRef.current].some(
            (sentId) => Number(message.id) > sentId
          )
        ) {
          return;
        }
        message.steps.forEach((step) => surfaceDirective(message.id, step));
      });
    },
    [surfaceDirective]
  );

  const armFlow = useCallback((step: CopilotStep) => {
    if (!step.flow) return;
    flowArmedRef.current = step.step === "done" ? null : { at: Date.now() };
  }, []);

  const refetch = useCallback(async () => {
    if (!enabled) return;
    try {
      const nextThread = await getCopilotThread();
      surfaceReplyDirectives(nextThread.messages);
      setThread(nextThread);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : translate("Nova could not load."));
    }
  }, [enabled, surfaceReplyDirectives]);

  useEffect(() => {
    if (!enabled) {
      setThread(null);
      setActiveDirective(null);
      sentMessageIdsRef.current.clear();
      shownDirectiveKeysRef.current.clear();
      pendingDirectiveRef.current = null;
      firedDirectiveKeysRef.current.clear();
      flowArmedRef.current = null;
      firedFlowArrivalsRef.current.clear();
      return;
    }
    setIsLoading(true);
    void refetch().finally(() => setIsLoading(false));
  }, [enabled, refetch]);

  useEffect(() => {
    const previousPathname = arrivalPathnameRef.current;
    arrivalPathnameRef.current = pathname;
    const pathnameChanged = Boolean(pathname && pathname !== previousPathname);
    if (!enabled || !pathnameChanged) return;

    const pending = pendingDirectiveRef.current;
    if (pending && Date.now() - pending.receivedAt > ARRIVAL_WINDOW_MS) {
      pendingDirectiveRef.current = null;
    } else if (
      pending &&
      TARGET_PATHS[pending.target] === pathname &&
      !firedDirectiveKeysRef.current.has(pending.key)
    ) {
      firedDirectiveKeysRef.current.add(pending.key);
      pendingDirectiveRef.current = null;
      void postCopilotArrival(pathname, pending.target)
        .then((accepted) => {
          if (accepted) onArrivalAccepted();
        })
        .catch(() => {
          // Stay quiet. The recruiter did not ask for this: they just walked to
          // a page. Telling them something failed would be noise about a thing
          // they never requested. They can always ask.
        });
      return;
    }

    const armed = flowArmedRef.current;
    if (!armed) return;
    if (Date.now() - armed.at > ARRIVAL_WINDOW_MS) {
      flowArmedRef.current = null;
      return;
    }
    const key = `${armed.at}:${pathname}`;
    if (firedFlowArrivalsRef.current.has(key)) return;
    firedFlowArrivalsRef.current.add(key);
    void postCopilotArrival(pathname)
      .then((accepted) => {
        if (accepted) onArrivalAccepted();
      })
      .catch(() => {
        // Stay quiet. The recruiter did not ask for this: they just walked to
        // a page. Telling them something failed would be noise about a thing
        // they never requested. They can always ask.
      });
  }, [enabled, onArrivalAccepted, pathname]);

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      const socket = new WebSocket(socketUrl());
      socketRef.current = socket;

      socket.onopen = async () => {
        try {
          const token = DISABLE_AUTH
            ? "dev"
            : await auth.currentUser?.getIdToken();
          if (!token) {
            socket.close(4401, "No Firebase token");
            return;
          }
          socket.send(JSON.stringify({ type: "auth", token }));
          reconnectAttemptRef.current = 0;
          setSocketRejected(false);
        } catch {
          socket.close(4401, "Token unavailable");
        }
      };

      socket.onmessage = (event) => {
        let payload: SocketEvent;
        try {
          payload = JSON.parse(event.data) as SocketEvent;
        } catch {
          return;
        }

        if (payload.type === "step") {
          armFlow(payload.step);
          surfaceDirective(payload.message_id, payload.step);
          setThread((current) => {
            if (!current) return current;
            const existing = current.messages.find(
              (message) => message.id === payload.message_id
            );
            const nextMessage = existing
              ? { ...existing, steps: mergeStep(existing.steps, payload.step) }
              : runningMessage(payload.message_id, payload.step);
            return {
              ...current,
              messages: mergeMessage(current.messages, nextMessage),
            };
          });
          return;
        }

        if (payload.message) {
          if (payload.message.role === "assistant") {
            payload.message.steps.forEach(armFlow);
          }
          surfaceReplyDirectives([payload.message]);
          setThread((current) =>
            current
              ? {
                  ...current,
                  messages: mergeMessage(current.messages, payload.message),
                }
              : current
          );
        } else if (payload.type === "error" && payload.error) {
          setNotice(payload.error);
          void refetch();
        }
      };

      socket.onclose = (event) => {
        if (socketRef.current === socket) socketRef.current = null;
        if (stopped) return;
        if (event.code === 4401) {
          setSocketRejected(true);
          void refetch();
          return;
        }
        const attempt = reconnectAttemptRef.current;
        const delay = RECONNECT_DELAYS[Math.min(attempt, RECONNECT_DELAYS.length - 1)];
        reconnectAttemptRef.current = attempt + 1;
        reconnectTimerRef.current = setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      stopped = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      socketRef.current?.close(1000, "Copilot unmounted");
      socketRef.current = null;
    };
  }, [armFlow, enabled, refetch, surfaceDirective, surfaceReplyDirectives]);

  const hasRunningMessage = Boolean(
    thread?.messages.some(
      (message) => message.run_status === "running" && !message.stale
    )
  );

  useEffect(() => {
    if (!enabled || (!socketRejected && !hasRunningMessage)) return;
    const interval = window.setInterval(() => void refetch(), 3_000);
    return () => window.clearInterval(interval);
  }, [enabled, hasRunningMessage, refetch, socketRejected]);

  const sendMessage = useCallback(
    async (text: string, page: string) => {
      const trimmed = text.trim();
      if (!trimmed || isSending) return false;
      setIsSending(true);
      setNotice("");
      try {
        const posted = await postCopilotMessage(trimmed, page);
        sentMessageIdsRef.current.add(posted.message_id);
        await refetch();
        return true;
      } catch (error) {
        if (error instanceof CopilotApiError && error.status === 409) {
          setNotice(translate("Nova is still working on your last request."));
        } else {
          setNotice(error instanceof Error ? error.message : translate("Message not sent."));
        }
        return false;
      } finally {
        setIsSending(false);
      }
    },
    [isSending, refetch]
  );

  const respondToProposal = useCallback(
    async (messageId: number | string, action: "confirm" | "decline") => {
      setProposalId(messageId);
      setNotice("");
      try {
        const result = await updateCopilotProposal(messageId, action);
        if (!result.ok) throw new Error(result.error || translate("Proposal could not be updated."));
        setThread((current) =>
          current
            ? {
                ...current,
                messages: current.messages.map((message) =>
                  message.id === messageId
                    ? {
                        ...message,
                        proposal_status: action === "confirm" ? "confirmed" : "declined",
                      }
                    : message
                ),
              }
            : current
        );
        return true;
      } catch (error) {
        setNotice(error instanceof Error ? error.message : translate("Proposal could not be updated."));
        await refetch();
        return false;
      } finally {
        setProposalId(null);
      }
    },
    [refetch]
  );

  const dismissDirective = useCallback(() => setActiveDirective(null), []);

  return {
    thread,
    isLoading,
    isSending,
    proposalId,
    notice,
    hasRunningMessage,
    sendMessage,
    respondToProposal,
    activeDirective,
    dismissDirective,
  };
}
